import { isAxiosError } from 'axios';
import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import ThermenRepository from 'src/data/repositories/ThermenRepository';
import CrashlyticsService from 'src/data/services/CrashlyticsService';
import type { ApiProfileData } from 'src/core/models/api/responses/ApiProfileResponse';
import { isApiValidationErrorResponse } from 'src/core/models/api/ApiValidationErrorResponse';
import Constants from 'src/core/utils/Constants';
import toErrorMessage from 'src/core/utils/ErrorMessages';
import { reportToCallerOrCrashlytics } from 'src/domain/stores/StoreErrorChannel';
import type ApiRequestError from 'src/core/models/api/ApiRequestError';

const noValidationErrors: Record<string, string[]> = {};

const initialScreenState = {
	isDeleteAccountDialogVisible: false,
	isDeletingAccount: false,
	deleteAccountPassword: '',
	deleteAccountError: null as string | null,
	isDeleteAccountPasswordVisible: false,
	isResetPasswordDialogVisible: false,
	isResettingPassword: false,
	resetPasswordSuccessful: null as boolean | null,
	isDatePickerVisible: false,
	datePickerDate: new Date(),
};

const initialState = {
	profile: null as ApiProfileData | null,
	draft: null as ApiProfileData | null,
	isLoading: false,
	isSaving: false,
	errorMessage: null as string | null,
	saveErrorMessage: null as string | null,
	validationErrors: noValidationErrors,
	...initialScreenState,
};

export const useProfileStore = create(
	combine({ ...initialState }, (set, get) => {
		const init = () => set({ ...initialScreenState });

		const toValidationErrors = (error: ApiRequestError): Record<string, string[]> | null =>
			isAxiosError(error) &&
			error.response?.status === Constants.httpStatusUnprocessableEntity &&
			isApiValidationErrorResponse(error.response.data)
				? error.response.data.errors
				: null;

		const applySaveError = (error: ApiRequestError) => {
			const validationErrors = toValidationErrors(error);

			if (validationErrors) {
				set({ validationErrors });

				return;
			}

			set({ saveErrorMessage: toErrorMessage(error) });
		};

		const getApiProfile = async ({
			onSuccess,
			onError,
			onFinally,
		}: {
			onSuccess?: () => void | Promise<void>;
			onError?: (error: ApiRequestError) => void;
			onFinally?: () => void;
		} = {}) => {
			if (get().profile === null) {
				set({ isLoading: true });
			}
			set({ errorMessage: null, validationErrors: {} });

			await ThermenRepository.getProfile()
				.then(async (response) => {
					set({ profile: response.data.data, isLoading: false });
					await onSuccess?.();
				})
				.catch((error: ApiRequestError) => {
					set({ errorMessage: toErrorMessage(error), isLoading: false });
					reportToCallerOrCrashlytics({ message: 'Fetching the profile failed.', error, onError });
				})
				.finally(() => onFinally?.());
		};

		const putApiPersonalData = async ({
			data,
			onSuccess,
			onError,
			onFinally,
		}: {
			data: Record<string, unknown>;
			onSuccess?: (data: ApiProfileData | null) => void | Promise<void>;
			onError?: (error: ApiRequestError) => void;
			onFinally?: () => void;
		}) => {
			set({ isSaving: true, saveErrorMessage: null, validationErrors: {} });

			await ThermenRepository.setPersonalData(data)
				.then(async (response) => {
					const profile = response.data.data ?? null;
					if (profile) {
						set({ profile, draft: null });
					}
					set({ isSaving: false });
					await onSuccess?.(profile);
				})
				.catch((error: ApiRequestError) => {
					applySaveError(error);
					set({ isSaving: false });
					reportToCallerOrCrashlytics({ message: 'Saving the personal data failed.', error, onError });
				})
				.finally(() => onFinally?.());
		};

		const putApiAddress = async ({
			data,
			onSuccess,
			onError,
			onFinally,
		}: {
			data: Record<string, unknown>;
			onSuccess?: (data: ApiProfileData | null) => void | Promise<void>;
			onError?: (error: ApiRequestError) => void;
			onFinally?: () => void;
		}) => {
			set({ isSaving: true, saveErrorMessage: null, validationErrors: {} });

			await ThermenRepository.setAddress(data)
				.then(async (response) => {
					const profile = response.data.data ?? null;
					if (profile) {
						set({ profile, draft: null });
					}
					set({ isSaving: false });
					await onSuccess?.(profile);
				})
				.catch((error: ApiRequestError) => {
					applySaveError(error);
					set({ isSaving: false });
					reportToCallerOrCrashlytics({ message: 'Saving the address failed.', error, onError });
				})
				.finally(() => onFinally?.());
		};

		const clearFieldValidationError = (key: string) => {
			const validationErrors = { ...get().validationErrors };
			delete validationErrors[key];
			set({ validationErrors });
		};

		const setPersonalField = ({ key, value }: { key: string; value: string }) => {
			const draft = get().draft ?? get().profile;
			const field = draft?.personal_data.fields[key];
			if (!draft || !field) {
				return;
			}

			set({
				draft: {
					...draft,
					personal_data: {
						...draft.personal_data,
						fields: { ...draft.personal_data.fields, [key]: { ...field, value } },
					},
				},
			});
			clearFieldValidationError(key);
		};

		const setAddressField = ({ key, value }: { key: string; value: string }) => {
			const draft = get().draft ?? get().profile;
			const field = draft?.address_data.fields[key];
			if (!draft || !field) {
				return;
			}

			set({
				draft: {
					...draft,
					address_data: {
						...draft.address_data,
						fields: { ...draft.address_data.fields, [key]: { ...field, value } },
					},
				},
			});
			clearFieldValidationError(key);
		};

		const discardDraft = () => set({ draft: null, saveErrorMessage: null, validationErrors: {} });

		const setDeleteAccountDialogVisible = (isDeleteAccountDialogVisible: boolean) =>
			set({
				isDeleteAccountDialogVisible,
				deleteAccountPassword: '',
				deleteAccountError: null,
				isDeleteAccountPasswordVisible: false,
			});

		const setDeletingAccount = (isDeletingAccount: boolean) => set({ isDeletingAccount });

		const setDeleteAccountPassword = (deleteAccountPassword: string) =>
			set({ deleteAccountPassword, deleteAccountError: null });

		const setDeleteAccountPasswordVisible = (isDeleteAccountPasswordVisible: boolean) =>
			set({ isDeleteAccountPasswordVisible });

		const applyDeleteAccountError = (error: ApiRequestError) => {
			const message = toValidationErrors(error)?.[Constants.validationFieldPassword]?.[0];

			// A rejected password is the expected outcome here, anything else is worth a report.
			if (!message) {
				CrashlyticsService.captureEvent({
					message: 'Deleting the account failed.',
					// The axios error carries the request body, so only scalars leave the device.
					extra: { status: error.response?.status, code: error.code, message: error.message },
				});
			}

			set({ deleteAccountError: message ?? toErrorMessage(error) });
		};

		const setResetPasswordDialogVisible = (isResetPasswordDialogVisible: boolean) =>
			set({ isResetPasswordDialogVisible });

		const setResettingPassword = (isResettingPassword: boolean) => set({ isResettingPassword });

		const setResetPasswordSuccessful = (resetPasswordSuccessful: boolean | null) => set({ resetPasswordSuccessful });

		const showDatePicker = (datePickerDate: Date) => set({ isDatePickerVisible: true, datePickerDate });

		const hideDatePicker = () => set({ isDatePickerVisible: false });

		const reset = () => set({ ...initialState });

		return {
			init,
			getApiProfile,
			putApiPersonalData,
			putApiAddress,
			setPersonalField,
			setAddressField,
			discardDraft,
			setDeleteAccountDialogVisible,
			setDeletingAccount,
			setDeleteAccountPassword,
			setDeleteAccountPasswordVisible,
			applyDeleteAccountError,
			setResetPasswordDialogVisible,
			setResettingPassword,
			setResetPasswordSuccessful,
			showDatePicker,
			hideDatePicker,
			reset,
		};
	}),
);
